import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { MessageCircle, User, Bot, Clock, AlertCircle, RefreshCw, Search, Filter, ThumbsUp, ThumbsDown, BarChart3, TrendingUp, Activity, Edit3, Download } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import MarkdownRenderer from './MarkdownRenderer';
import axios from 'axios';

const LogsContent = () => {
  const [logs, setLogs] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('recent'); // recent, oldest
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all'); // Filtre par utilisateur
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(10); // Nombre de logs par page

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger les logs et corrections
  const loadLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [logsResponse, correctionsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/logs?all_logs=true`),
        axios.get(`${BACKEND_URL}/api/dataset`)
      ]);
      
      setLogs(logsResponse.data.logs || []);
      setCorrections(correctionsResponse.data.corrections || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des logs de conversation');
    } finally {
      setIsLoading(false);
    }
  }, [BACKEND_URL]);

  // Calculer les KPI
  const calculateKPIs = useCallback(() => {
    // Filtrer les logs selon l'utilisateur sélectionné
    const logsToAnalyze = selectedUser === 'all' 
      ? logs 
      : logs.filter(log => log.username === selectedUser);
    
    const totalExchanges = logsToAnalyze.length;
    const negativeExchanges = logsToAnalyze.filter(log => log.rating === 0).length;
    const noFeedbackExchanges = logsToAnalyze.filter(log => log.rating === undefined || log.rating === null).length;
    const explicitPositiveExchanges = logsToAnalyze.filter(log => log.rating === 1).length;
    
    // Les "pas de feedback" sont comptabilisés comme positifs
    const positiveExchanges = explicitPositiveExchanges + noFeedbackExchanges;
    
    const positivePercentage = totalExchanges > 0 ? ((positiveExchanges / totalExchanges) * 100) : 0;
    const negativePercentage = totalExchanges > 0 ? ((negativeExchanges / totalExchanges) * 100) : 0;
    const noFeedbackPercentage = totalExchanges > 0 ? ((noFeedbackExchanges / totalExchanges) * 100) : 0;
    
    // Calculer les corrections/modifications
    const totalCorrections = corrections.length;
    const correctionPercentage = totalExchanges > 0 ? ((totalCorrections / totalExchanges) * 100) : 0;
    
    // Calculer les échanges des dernières 24h
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const recentExchanges = logsToAnalyze.filter(log => {
      try {
        const logDate = new Date(log.timestamp);
        return logDate >= yesterday;
      } catch {
        return false;
      }
    }).length;

    // Nouveau KPI : Nombre d'utilisateurs uniques
    const uniqueUsers = [...new Set(logs.map(log => log.username).filter(u => u && u !== 'anonymous'))];
    const totalUsers = uniqueUsers.length;
    
    // Nouveau KPI : Moyenne de requêtes par utilisateur
    const avgRequestsPerUser = totalUsers > 0 ? (logs.length / totalUsers) : 0;
    
    // Nouveau KPI : Moyenne de requêtes par utilisateur par jour
    // Calculer le nombre de jours avec des logs
    const dates = logs.map(log => {
      try {
        return new Date(log.timestamp).toDateString();
      } catch {
        return null;
      }
    }).filter(d => d !== null);
    
    const uniqueDays = [...new Set(dates)].length;
    const avgRequestsPerUserPerDay = (totalUsers > 0 && uniqueDays > 0) 
      ? (logs.length / totalUsers / uniqueDays) 
      : 0;

    return {
      totalExchanges,
      positiveExchanges,
      explicitPositiveExchanges,
      negativeExchanges,
      noFeedbackExchanges,
      totalCorrections,
      positivePercentage: Math.round(positivePercentage * 10) / 10,
      negativePercentage: Math.round(negativePercentage * 10) / 10,
      noFeedbackPercentage: Math.round(noFeedbackPercentage * 10) / 10,
      correctionPercentage: Math.round(correctionPercentage * 10) / 10,
      recentExchanges,
      totalUsers,
      avgRequestsPerUser: Math.round(avgRequestsPerUser * 10) / 10,
      avgRequestsPerUserPerDay: Math.round(avgRequestsPerUserPerDay * 10) / 10
    };
  }, [logs, corrections, selectedUser]);

  // Filtrer et trier les logs
  useEffect(() => {
    let filtered = logs;

    // Filtrage par utilisateur
    if (selectedUser !== 'all') {
      filtered = filtered.filter(log => log.username === selectedUser);
    }

    // Filtrage par terme de recherche
    if (searchTerm.trim()) {
      filtered = filtered.filter(log => 
        log.user_query?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.original_response?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Tri
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });

    setFilteredLogs(filtered);
    setCurrentPage(1); // Revenir à la première page lors d'un changement de filtre
  }, [logs, searchTerm, sortOrder, selectedUser]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Formatage de la date
  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  // Tronquer le texte pour l'aperçu
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Trouver une correction associée à un log
  const findCorrectionForLog = (log) => {
    return corrections.find(correction => 
      correction.user_query === log.user_query && 
      correction.original_response === log.original_response
    );
  };

  // Extraire les utilisateurs uniques
  const uniqueUsers = [...new Set(logs.map(log => log.username).filter(u => u && u !== 'anonymous'))];

  const kpis = calculateKPIs();

  return (
    <div className="space-y-6">
      {/* Section KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {/* Total des échanges */}
        <Card className="border border-blue-200 bg-blue-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-600 truncate">Total échanges</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">{kpis.totalExchanges}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pourcentage positif */}
        <Card className="border border-green-200 bg-green-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-green-600 truncate">Feedback positif</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">{kpis.positivePercentage}%</p>
                <p className="text-[10px] sm:text-xs text-green-600">
                  ({kpis.positiveExchanges} sur {kpis.totalExchanges})
                  {kpis.noFeedbackExchanges > 0 && (
                    <span className="block">dont {kpis.noFeedbackExchanges} sans feedback</span>
                  )}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <ThumbsUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pourcentage négatif */}
        <Card className="border border-red-200 bg-red-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-red-600 truncate">Feedback négatif</p>
                <p className="text-xl sm:text-2xl font-bold text-red-900">{kpis.negativePercentage}%</p>
                <p className="text-[10px] sm:text-xs text-red-600">({kpis.negativeExchanges} sur {kpis.totalExchanges})</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <ThumbsDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Échanges récents (24h) */}
        <Card className="border border-orange-200 bg-orange-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-orange-600 truncate">Dernières 24h</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-900">{kpis.recentExchanges}</p>
                <p className="text-[10px] sm:text-xs text-orange-600">
                  {kpis.totalExchanges > 0 ? Math.round((kpis.recentExchanges / kpis.totalExchanges) * 100) : 0}% du total
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Corrections/Modifications */}
        <Card className="border border-purple-200 bg-purple-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-purple-600 truncate">Modifications</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-900">{kpis.totalCorrections}</p>
                <p className="text-[10px] sm:text-xs text-purple-600">
                  {kpis.correctionPercentage}% des échanges
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Edit3 className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nombre d'utilisateurs */}
        <Card className="border border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-indigo-600 truncate">Utilisateurs</p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-900">{kpis.totalUsers}</p>
                <p className="text-[10px] sm:text-xs text-indigo-600">
                  Utilisateurs actifs
                </p>
              </div>
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Moyenne requêtes par utilisateur */}
        <Card className="border border-cyan-200 bg-cyan-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-cyan-600 truncate">Moy. par utilisateur</p>
                <p className="text-xl sm:text-2xl font-bold text-cyan-900">{kpis.avgRequestsPerUser}</p>
                <p className="text-[10px] sm:text-xs text-cyan-600">
                  Requêtes / utilisateur
                </p>
              </div>
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Moyenne requêtes par utilisateur par jour */}
        <Card className="border border-teal-200 bg-teal-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-teal-600 truncate">Moy. / utilisateur / jour</p>
                <p className="text-xl sm:text-2xl font-bold text-teal-900">{kpis.avgRequestsPerUserPerDay}</p>
                <p className="text-[10px] sm:text-xs text-teal-600">
                  Requêtes / utilisateur / jour
                </p>
              </div>
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graphique de répartition des feedbacks */}
      {kpis.totalExchanges > 0 && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-lg">
              <BarChart3 className="w-5 h-5 text-slate-600" />
              <span>Répartition des feedbacks</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Barre de progression pour feedback positif */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-600 font-medium">Feedback positif</span>
                  <span className="text-green-900 font-semibold">{kpis.positiveExchanges} ({kpis.positivePercentage}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${kpis.positivePercentage}%` }}
                  ></div>
                </div>
              </div>

              {/* Barre de progression pour feedback négatif */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-red-600 font-medium">Feedback négatif</span>
                  <span className="text-red-900 font-semibold">{kpis.negativeExchanges} ({kpis.negativePercentage}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${kpis.negativePercentage}%` }}
                  ></div>
                </div>
              </div>

              {/* Détail des feedbacks positifs */}
              {kpis.noFeedbackExchanges > 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">
                    <span className="font-medium">Détail positifs:</span> {kpis.explicitPositiveExchanges} avec pouce levé + {kpis.noFeedbackExchanges} sans feedback = {kpis.positiveExchanges} total
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header avec contrôles */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span>Logs</span>
              <Badge variant="secondary">
                {filteredLogs.length}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const headers = ['Timestamp', 'Utilisateur', 'Question', 'Réponse', 'Feedback', 'Réponse corrigée'];
                  const rows = filteredLogs.map(log => [
                    log.timestamp || '',
                    log.username || '',
                    '"' + (log.user_query || '').replace(/"/g, '""') + '"',
                    '"' + (log.original_response || '').replace(/"/g, '""') + '"',
                    log.rating === 1 ? 'Positif' : log.rating === 0 ? 'Négatif' : '-',
                    '"' + (log.corrected_response || '').replace(/"/g, '""') + '"'
                  ]);
                  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
                  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'logs_axia_' + new Date().toISOString().slice(0,10) + '.csv';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                disabled={filteredLogs.length === 0}
                className="flex items-center gap-2"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export CSV</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadLogs}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Actualiser</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {/* Filtre utilisateur */}
              <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                <User className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les utilisateurs</SelectItem>
                    {uniqueUsers.map(user => (
                      <SelectItem key={user} value={user}>
                        {user}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            
              {/* Tri */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Filter className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Plus récents</SelectItem>
                    <SelectItem value="oldest">Plus anciens</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages d'erreur */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
          <p className="text-slate-500">Chargement des logs...</p>
        </div>
      )}

      {/* Liste des logs */}
      {!isLoading && filteredLogs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchTerm ? 'Aucun log trouvé pour cette recherche' : 'Aucun log de conversation disponible'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Logs paginés */}
          {filteredLogs
            .slice((currentPage - 1) * logsPerPage, currentPage * logsPerPage)
            .map((log, index) => {
              const globalIndex = (currentPage - 1) * logsPerPage + index;
              return (
            <Card key={log.id || index} className="border border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-3 sm:p-6">
                {/* Header du log */}
                <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
                  <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
                    #{globalIndex + 1}
                  </Badge>
                  {log.username && (
                    <div className="flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded-full">
                      <User className="w-3 h-3" />
                      <span className="text-xs font-medium">{log.username}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-xs sm:text-sm text-slate-500">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{formatDate(log.timestamp)}</span>
                  </div>
                  {log.rating === 1 ? (
                    <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      <ThumbsUp className="w-3 h-3" />
                      <span className="text-xs font-medium hidden sm:inline">Positif</span>
                    </div>
                  ) : log.rating === 0 ? (
                    <div className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full">
                      <ThumbsDown className="w-3 h-3" />
                      <span className="text-xs font-medium hidden sm:inline">Négatif</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      <span className="text-xs">-</span>
                    </div>
                  )}
                  <Badge variant="secondary" className="text-[10px] ml-auto">
                    {log.id ? log.id.substring(0, 8) : 'N/A'}
                  </Badge>
                </div>

                {/* Question utilisateur */}
                <div className="mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-slate-700">Question utilisateur</span>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">
                      {log.user_query || 'Question non disponible'}
                    </p>
                  </div>
                </div>

                {/* Réponse assistant */}
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Bot className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">Réponse d'Axia</span>
                    <Badge variant="outline" className="text-xs">
                      {log.original_response?.length || 0} caractères
                    </Badge>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    {log.original_response ? (
                      <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                        <MarkdownRenderer 
                          content={log.original_response} 
                          className="text-sm"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">Réponse non disponible</p>
                    )}
                  </div>
                </div>

                {/* Correction/Rectification */}
                {(() => {
                  const correction = findCorrectionForLog(log);
                  return correction && (
                    <div className="mt-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Edit3 className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-slate-700">Rectification</span>
                        <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700 text-xs">
                          Modifiée le {formatDate(correction.timestamp)}
                        </Badge>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        {correction.corrected_response ? (
                          <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                            <MarkdownRenderer 
                              content={correction.corrected_response} 
                              className="text-sm"
                            />
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 italic">Correction non disponible</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Informations supplémentaires */}
                {log.session_id && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center space-x-4 text-xs text-slate-500">
                      <span>Session: {log.session_id}</span>
                      {log.created_at && (
                        <span>Créé: {formatDate(log.created_at)}</span>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}

          {/* Contrôles de pagination */}
          {filteredLogs.length > logsPerPage && (
            <Card className="mt-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    Affichage de {Math.min((currentPage - 1) * logsPerPage + 1, filteredLogs.length)} à{' '}
                    {Math.min(currentPage * logsPerPage, filteredLogs.length)} sur {filteredLogs.length} logs
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      Première
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Précédent
                    </Button>
                    <span className="text-sm text-slate-600 px-3">
                      Page {currentPage} sur {Math.ceil(filteredLogs.length / logsPerPage)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredLogs.length / logsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(filteredLogs.length / logsPerPage)}
                    >
                      Suivant
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.ceil(filteredLogs.length / logsPerPage))}
                      disabled={currentPage >= Math.ceil(filteredLogs.length / logsPerPage)}
                    >
                      Dernière
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default LogsContent;